import {services} from '../../../api';
import {
  GetSummariesByDateBody,
  SummariesByDateData,
  SummaryDetailsItemType,
} from '../../../interfaces/api/summaries';
import {
  GetPredictionsByDateBody,
  PredictionDetailsItemType,
  PredictionsByDateData,
} from '../../../interfaces/api/predictions';
import {
  AnalysisByDateData,
  AnalysisDetailsItemType,
  GetAnalysisByDateBody,
} from '../../../interfaces/api/analysis';

/**------------ Summaries ------------**/

/**
 * this function to get Summary Details By id
 *
 * @return {SummaryDetailsItemType | null} result of Summary Details
 */
export const getSummaryDetails = async (
  id: number,
): Promise<SummaryDetailsItemType | null> => {
  try {
    if (!id) {
      throw new Error('id is required');
    }
    const res = await services.summaries.getById(id);
    return res?.content;
  } catch (error: any) {
    return null;
  }
};

/**
 * this function to get Summaries List By Date and tab id
 *
 * @return {SummariesByDateData | null} result of Summaries List
 */
export const getSummariesByDate = async (
  body: GetSummariesByDateBody,
): Promise<SummariesByDateData | null> => {
  try {
    if (!body?.tabId || !body?.dateFrom) {
      throw new Error('tabId and dateFrom are required');
    }
    const res = await services.summaries.getByDate(body);
    return res?.content;
  } catch (error: any) {
    return null;
  }
};

/**------------ Predictions ------------**/

/**
 * This function retrieves Prediction Details by ID.
 *
 * @return {PredictionDetailsItemType | null} Result of Prediction Details.
 */
export const getPredictionDetails = async (
  id: number,
): Promise<PredictionDetailsItemType | null> => {
  try {
    if (!id) {
      throw new Error('id is required');
    }
    const res = await services.predictions.getById(id);
    return {
      summary: res?.content?.predict || '',
      summarydate: res?.content?.predictdate || '',
      ...(res?.content || {}),
    };
  } catch (error: any) {
    return null;
  }
};

/**
 * This function retrieves Predictions List by Date and Tab ID.
 *
 * @return {PredictionsByDateData | null} Result of Predictions List.
 */
export const getPredictionsByDate = async (
  body: GetPredictionsByDateBody,
): Promise<PredictionsByDateData | null> => {
  try {
    if (!body?.tabId || !body?.dateFrom) {
      throw new Error('tabId and dateFrom are required');
    }
    const res = await services.predictions.getByDate(body);
    const data = (res?.content?.data || [])?.map(i => {
      return {
        summary: i?.predict,
        summarydate: i?.predictdate,
        ...(i || {}),
      };
    });

    return {...(res?.content || {}), data};
  } catch (error: any) {
    return null;
  }
};

/**------------ Analysis ------------**/

/**
 * This function retrieves Analysis Details by ID.
 *
 * @return {AnalysisDetailsItemType | null} Result of Analysis Details.
 */
export const getAnalysisDetails = async (
  id: number,
): Promise<AnalysisDetailsItemType | null> => {
  try {
    if (!id) {
      throw new Error('id is required');
    }
    const res = await services.analysis.getById(id);

    return {
      summary: res?.content?.analysis || '',
      summarydate: res?.content?.analysisdate || res?.content?.analysisDate,
      ...(res?.content || {}),
    };
  } catch (error: any) {
    return null;
  }
};

/**
 * This function retrieves Analysis List by Date and Tab ID.
 *
 * @return {AnalysisByDateData | null} Result of Analysis List.
 */
export const getAnalysisByDate = async (
  body: GetAnalysisByDateBody,
): Promise<AnalysisByDateData | null> => {
  try {
    if (!body?.tabId || !body?.dateFrom) {
      throw new Error('tabId and dateFrom are required');
    }
    const res = await services.analysis.getByDate(body);
    const data = (res?.content?.data || [])?.map(i => {
      return {
        summary: i?.analysis,
        summarydate: i.analysisdate,
        ...(i || {}),
      };
    });

    return {...(res?.content || {}), data};
  } catch (error: any) {
    return null;
  }
};
